import React, { useState } from 'react';
import { X, Mail, Phone, User, MapPin, Heart, Calendar, Users } from 'lucide-react';

const SubscriptionModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    spouseFirstName: '',
    spouseLastName: '',
    email: '',
    phone: '',
    city: '',
    marriageDate: '',
    children: '',
    interests: [],
    testimony: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const interests = [
    { id: 'lectures', label: 'Lectures quotidiennes', icon: '📖' },
    { id: 'events', label: 'Événements communautaires', icon: '🎉' },
    { id: 'prayers', label: 'Groupes de prière', icon: '🙏' },
    { id: 'formation', label: 'Formation matrimoniale', icon: '💍' },
    { id: 'pilgrimage', label: 'Pèlerinages', icon: '✈️' },
    { id: 'youth', label: 'Activités jeunes', icon: '👦👧' }
  ];

  const beninCities = [
    'Porto-Novo', 'Cotonou', 'Parakou', 'Djougou', 'Bohicon', 'Kandi',
    'Ouidah', 'Abomey', 'Natitingou', 'Lokossa', 'Savalou', 'Pobè'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInterestChange = (interestId) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(id => id !== interestId)
        : [...prev.interests, interestId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Ici vous pourriez envoyer les données à votre API
    console.log('Données du formulaire:', formData);
    alert('Merci pour votre inscription ! Nous vous contacterons bientôt. Que Dieu bénisse votre couple ! 🙏');
    onClose();
    // Reset form
    setFormData({
      firstName: '',
      lastName: '',
      spouseFirstName: '',
      spouseLastName: '',
      email: '',
      phone: '',
      city: '',
      marriageDate: '',
      children: '',
      interests: [],
      testimony: ''
    });
    setCurrentStep(1);
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.firstName && formData.lastName && formData.spouseFirstName && formData.spouseLastName;
      case 2:
        return formData.email && formData.phone && formData.city;
      case 3:
        return formData.interests.length > 0;
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-fsf-primary to-fsf-secondary text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Heart className="w-8 h-8 animate-heart-beat" />
              <div>
                <h2 className="text-2xl font-bold font-serif">Rejoignez la famille FSF</h2>
                <p className="text-fsf-light">Communauté de couples chrétiens au Bénin 🇧🇯</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Indicateur de progression */}
          <div className="mt-6">
            <div className="flex items-center space-x-2 mb-2">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`flex-1 h-2 rounded-full ${
                    step <= currentStep ? 'bg-fsf-warm' : 'bg-white/20'
                  }`}
                />
              ))}
            </div>
            <p className="text-fsf-light text-sm">
              Étape {currentStep} sur {totalSteps}
            </p>
          </div>
        </div>

        {/* Corps du formulaire */}
        <form onSubmit={handleSubmit} className="p-6">
          
          {/* Étape 1: Informations du couple */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-fsf-primary mb-2 font-serif">
                  👨‍👩‍💑 Informations du couple
                </h3>
                <p className="text-gray-600">Parlez-nous de votre union bénie</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Prénom de l'époux *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fsf-primary focus:border-fsf-primary"
                    placeholder="Prénom de l'époux"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'époux *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fsf-primary focus:border-fsf-primary"
                    placeholder="Nom de famille"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Prénom de l'épouse *
                  </label>
                  <input
                    type="text"
                    name="spouseFirstName"
                    value={formData.spouseFirstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fsf-primary focus:border-fsf-primary"
                    placeholder="Prénom de l'épouse"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'épouse *
                  </label>
                  <input
                    type="text"
                    name="spouseLastName"
                    value={formData.spouseLastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fsf-primary focus:border-fsf-primary"
                    placeholder="Nom de l'épouse"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Date de mariage
                </label>
                <input
                  type="date"
                  name="marriageDate"
                  value={formData.marriageDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fsf-primary focus:border-fsf-primary"
                />
              </div>
            </div>
          )}

          {/* Étape 2: Contact et localisation */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-fsf-primary mb-2 font-serif">
                  📞 Informations de contact
                </h3>
                <p className="text-gray-600">Comment pouvons-nous vous joindre ?</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Adresse email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fsf-primary focus:border-fsf-primary"
                  placeholder="votre.email@exemple.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Numéro de téléphone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fsf-primary focus:border-fsf-primary"
                  placeholder="+229 XX XX XX XX"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Ville de résidence *
                </label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fsf-primary focus:border-fsf-primary"
                  required
                >
                  <option value="">Choisissez votre ville</option>
                  {beninCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Users className="w-4 h-4 inline mr-2" />
                  Nombre d'enfants
                </label>
                <select
                  name="children"
                  value={formData.children}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fsf-primary focus:border-fsf-primary"
                >
                  <option value="">Sélectionnez</option>
                  <option value="0">Aucun enfant</option>
                  <option value="1">1 enfant</option>
                  <option value="2">2 enfants</option>
                  <option value="3">3 enfants</option>
                  <option value="4+">4 enfants ou plus</option>
                </select>
              </div>
            </div>
          )}

          {/* Étape 3: Intérêts et témoignage */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-fsf-primary mb-2 font-serif">
                  ⭐ Vos intérêts dans la communauté
                </h3>
                <p className="text-gray-600">Que souhaitez-vous partager avec nous ?</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Activités qui vous intéressent * (sélectionnez au moins une)
                </label>
                <div className="grid md:grid-cols-2 gap-3">
                  {interests.map(interest => (
                    <label
                      key={interest.id}
                      className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.interests.includes(interest.id)
                          ? 'border-fsf-primary bg-fsf-light text-fsf-primary'
                          : 'border-gray-200 hover:border-fsf-primary/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.interests.includes(interest.id)}
                        onChange={() => handleInterestChange(interest.id)}
                        className="sr-only"
                      />
                      <span className="text-xl">{interest.icon}</span>
                      <span className="font-medium">{interest.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  💝 Témoignage ou attentes (optionnel)
                </label>
                <textarea
                  name="testimony"
                  value={formData.testimony}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fsf-primary focus:border-fsf-primary"
                  placeholder="Partagez avec nous votre témoignage de couple chrétien ou vos attentes envers la communauté FSF..."
                />
              </div>
            </div>
          )}

          {/* Boutons de navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={prevStep}
              className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              disabled={currentStep === 1}
            >
              Précédent
            </button>

            {currentStep === totalSteps ? (
              <button
                type="submit"
                className="px-8 py-3 bg-gradient-to-r from-fsf-primary to-fsf-secondary text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200 disabled:opacity-50"
                disabled={!isStepValid()}
              >
                🙏 Rejoindre la famille FSF
              </button>
            ) : (
              <button
                type="button"
                onClick={nextStep}
                className="px-6 py-3 bg-fsf-primary text-white rounded-lg font-medium hover:bg-fsf-secondary transition-colors duration-200 disabled:opacity-50"
                disabled={!isStepValid()}
              >
                Suivant
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionModal;